import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { 
  Maximize2, 
  Minimize2, 
  Tv, 
  SkipForward, 
  Users, 
  Music, 
  X, 
  Sparkles 
} from "lucide-react";
import { usePartyStore } from "../../stores/partyStore";
import { removeSongFromQueue } from "../../lib/firebase";
import { YouTubePlayer } from "./YouTubePlayer";
import { SingerCueBanner } from "./SingerCueBanner";
import { ThemeSwitcher } from "../common/ThemeSwitcher";
import { UserAvatar } from "../common/UserAvatar";

export const TVDisplayView: React.FC = () => {
  const { currentParty, userRole, queue, members, currentSong, setTVMode } = usePartyStore();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const partyCode = currentParty?.partyCode || "";
  const joinUrl = `${window.location.origin}/?partyCode=${partyCode}`;
  const nextSong = queue.length > 1 ? queue[1] : null;
  const isHost = userRole === "HOST";

  const handleSkipSong = async () => {
    if (!currentParty || !currentSong) return;
    const songIdToDelete = currentSong.id;

    try {
      // Optimistic local state update for instant transition
      const nextQueue = queue.filter((q) => q.id !== songIdToDelete);
      usePartyStore.getState().setQueue(nextQueue);
      usePartyStore.getState().setCurrentSong(nextQueue.length > 0 ? nextQueue[0] : null);

      await removeSongFromQueue(currentParty.id, songIdToDelete);
    } catch (err) {
      console.error("Error advancing song:", err);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(console.error);
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(console.error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background text-text-main flex flex-col p-4 sm:p-6 overflow-hidden select-none">
      {/* TV Header Bar */}
      <div className="flex items-center justify-between pb-3 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white shadow-lg">
            <Tv className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl sm:text-3xl font-bold font-heading tracking-wider">
                {currentParty?.partyName || "Karaoke Party"}
              </h2>
              <span className="bg-primary/20 text-primary border border-primary/40 text-xs font-mono font-bold px-2 py-0.5 rounded-lg tracking-widest">
                {partyCode}
              </span>
            </div>
            <p className="text-[11px] text-text-muted">
              TV Display Mode • Scan QR to join and add songs
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {isHost && currentSong && (
            <button
              onClick={handleSkipSong}
              className="py-2 px-3.5 rounded-xl bg-card border border-border hover:border-primary text-text-main text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
              title="Skip to next song"
            >
              <SkipForward className="w-4 h-4 text-primary" />
              <span className="hidden sm:inline">Next Song</span>
            </button>
          )}

          <ThemeSwitcher />

          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-xl bg-card border border-border hover:border-primary text-text-muted hover:text-text-main transition-colors cursor-pointer"
            title="Toggle Fullscreen"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          <button
            onClick={() => setTVMode(false)}
            className="p-2 rounded-xl bg-card border border-border hover:border-red-400 text-text-muted hover:text-red-400 transition-colors cursor-pointer"
            title="Exit TV Mode"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 min-h-0">
        {/* Left Side: Video Player & Singer Cues */}
        <div className="lg:col-span-8 flex flex-col justify-between space-y-4 min-h-0">
          <div className="flex-1 flex items-center justify-center min-h-0">
            <YouTubePlayer
              currentSong={currentSong}
              onSongEnd={handleSkipSong}
              isHost={isHost}
            />
          </div>

          <SingerCueBanner currentSong={currentSong} nextSong={nextSong} />
        </div>

        {/* Right Side: QR Code, Queue & Members */}
        <div className="lg:col-span-4 flex flex-col gap-4 overflow-y-auto pr-1">
          {/* QR Code Card */}
          <div className="bg-card p-5 rounded-3xl border border-border shadow-xl flex items-center gap-4">
            <div className="p-2 bg-white rounded-2xl shadow-md shrink-0">
              <QRCodeSVG
                value={joinUrl}
                size={110}
                level="M"
                includeMargin={false}
              />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-accent flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Scan to Join
              </span>
              <h4 className="text-2xl font-bold font-mono tracking-widest text-text-main">
                {partyCode}
              </h4>
              <p className="text-[11px] text-text-muted leading-tight">
                Scan QR code with your phone camera to add songs instantly!
              </p>
            </div>
          </div>

          {/* Upcoming Queue Card */}
          <div className="bg-card p-5 rounded-3xl border border-border shadow-xl flex-1 flex flex-col min-h-[220px]">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-border">
              <h3 className="text-lg font-bold font-heading text-text-main flex items-center gap-2">
                <Music className="w-4 h-4 text-primary" />
                Up Next ({queue.length})
              </h3>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto pr-1 max-h-[250px]">
              {queue.length === 0 ? (
                <p className="text-xs text-text-muted italic text-center py-6">
                  No songs in queue yet. Be the first to add!
                </p>
              ) : (
                queue.map((song, idx) => (
                  <div
                    key={song.id}
                    className={`p-2.5 rounded-2xl flex items-center gap-3 border transition-all ${
                      idx === 0
                        ? "bg-primary/10 border-primary/40 text-text-main"
                        : "bg-background/50 border-border/50 text-text-muted"
                    }`}
                  >
                    <span className="text-xs font-mono font-bold w-5 text-center">
                      {idx + 1}
                    </span>
                    <img
                      src={song.thumbnailUrl}
                      alt={song.title}
                      className="w-10 h-8 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate text-text-main">
                        {song.title}
                      </p>
                      <p className="text-[10px] truncate text-text-muted">
                        Added by {song.addedByUserName}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Party Members Live Row */}
          <div className="bg-card p-4 rounded-3xl border border-border shadow-xl">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-border/60">
              <span className="text-xs font-bold font-heading text-text-main flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-accent" />
                Singers in Room ({members.length})
              </span>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto py-1">
              {members.map((m) => (
                <div key={m.uid} className="shrink-0" title={`${m.name} (${m.role})`}>
                  <UserAvatar
                    photoURL={m.photoURL}
                    name={m.name}
                    uid={m.uid}
                    size="md"
                    isHost={m.role === "HOST"}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
